import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
  LineChart,
  AreaChart,
  Area,
  ScatterChart,
  Scatter,
  LabelList,
} from "recharts";

//minutes to hours change in y axis value common function
const minutesToHour = (totalMinutes) => {
  const hours = (totalMinutes / 60).toFixed(2); // keep 2 decimal places
  return `${hours}`;
};
const minutesToTimeString = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}`;
};

const CustomTooltips = ({ active, payload }) => {
  if (active && payload && payload.length > 0) {
    const value = Number(payload[0].value);
    const time = minutesToTimeString(value);
    const recName = payload[0].payload.RECNAME || payload[0].payload.FNAME;
    return (
      <div style={{ background: "#fff", border: "1px solid #ccc", padding: 8 }}>
        <p style={{ margin: 0 }}>{`${recName}: ${time}`}</p>
      </div>
    );
  }
  return null;
};

//Total Customer vertical bar chart
export const TotalCustomerChart = ({
  data,
  width = "100%",
  barColor = "#9747FF",
}) => {
  if (!data || data.length === 0) {
    return <p>No data available</p>;
  }

  const barSize = 28;
  const gap = 12;

  // Dynamic height
  const calculatedHeight = data.length * (barSize + gap);
  const minHeight = 200;
  const chartHeight = Math.max(calculatedHeight, minHeight);

  return (
    <div
      style={{
        height: chartHeight > 350 ? 350 : chartHeight,
        overflowY: chartHeight > 350 ? "auto" : "hidden",
      }}
    >
      <ResponsiveContainer width={width} height={chartHeight}>
        <BarChart
          layout="vertical"
          data={data}
          barSize={barSize}
          margin={{ top: 20, right: 50, left: 20, bottom: 40 }}
        >
          <CartesianGrid vertical={false} />

          {/* Number Axis */}
          <XAxis
            type="number"
            allowDecimals={false}
            axisLine={true}
            tickLine={false}
            domain={[0, "dataMax"]}
            label={{
              value: "Trips Done",
              position: "left",
              offset: 15,
              style: { fontSize: 14 }
            }}
          />

          {/* Category Axis */}
          <YAxis
            type="category"
            dataKey="FNAME"
            axisLine={false}
            tickLine={false}
            width={200}
          />

          <Bar dataKey="Completed" fill={barColor} radius={4}>
            <LabelList
              position="right"
              style={{ fontSize: 12 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const CustomerPieChart = ({
  data = [],
  colors = [],
  width = 500,
  height = 350,
  innerRadius = 50,
  outerRadius = 70,
}) => {
  const [activeIndex, setActiveIndex] = useState(null);

  // const onPieEnter = (_, index) => setActiveIndex(index);
  // const onPieLeave = () => setActiveIndex(null);
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const value = payload[0]?.payload?.Completed;
      const time = minutesToTimeStrings(value);
      const recName = payload[0]?.payload?.RECNAME || payload[0].payload.FNAME;
      return (
        <div
          style={{
            background: "#E7E7ED",
            color: "#5F6D8C",
            padding: "10px",
            borderRadius: "8px",
            fontSize: "12px",
            // minWidth: "60px",
            textAlign: "center",
            boxShadow: "0px 2px 10px rgba(0,0,0,0.3)",
          }}
        >
          <p style={{ margin: 0 }}>{`${recName} ${time}`}</p>
        </div>
      );
    }
    return null;
  };
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    index,
  }) => {
    if (!data[index]) return null;

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 2;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    const isRightSide = Math.cos(-midAngle * RADIAN) > 0;
    const lineLength = isRightSide ? 18 : -18;
    const textOffset = isRightSide ? 18 : -18;
    const color = colors[index % colors.length] || "#ccc";
    const squareSize = 10;

    const fullName = data[index].RECNAME || data[index].FNAME;
    const words = fullName?.split(" ") || [];
    const firstLine = words.slice(0, 2).join(" ");
    const secondLine = words.slice(2).join(" ");
    const timeLabel = minutesToTimeString(data[index].Completed);

    const lineSpacing = 12; // <-- Increased spacing to prevent overlap

    return (
      <>
        <line
          x1={x}
          y1={y}
          x2={x + lineLength}
          y2={y}
          stroke="#ccc"
          strokeWidth={1}
        />
        <rect
          x={x + lineLength + (isRightSide ? 0 : -squareSize)}
          y={y - squareSize / 2}
          width={squareSize}
          height={squareSize}
          fill={color}
          rx={2}
          ry={2}
        />
        {/* <text
        x={x + lineLength + textOffset}
        y={y - lineSpacing}
        fill="#5F6D8C"
        textAnchor={isRightSide ? "start" : "end"}
        fontSize={11}
      >
        {firstLine}
      </text> */}
        {/* First line (Name + time) */}
        <text
          x={x + lineLength + textOffset}
          y={y - lineSpacing + 15} // draw this ABOVE
          fill="#5F6D8C"
          textAnchor={isRightSide ? "start" : "end"}
          fontSize={11}
        >
          {`${firstLine} (${timeLabel})`}
        </text>

        {/* Second line below only if exists */}
        {secondLine && (
          <text
            x={x + lineLength + textOffset}
            y={y + 15} // draw slightly below first line
            fill="#5F6D8C"
            textAnchor={isRightSide ? "start" : "end"}
            fontSize={11}
          >
            {secondLine}
          </text>
        )}
      </>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          cx={width / 2}
          cy={height / 2}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          // paddingAngle={-15}
          // cornerRadius={40}
          dataKey="Completed"
          label={renderCustomizedLabel}
          // onMouseEnter={onPieEnter}
          // onMouseLeave={onPieLeave}
          style={{ cursor: "pointer" }}
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={colors[index % colors.length]}
              stroke={
                index === activeIndex ? colors[index % colors.length] : "none"
              }
              strokeWidth={index === activeIndex ? 3 : 1}
            />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
      </PieChart>
    </ResponsiveContainer>
  );
};
export const CustomerPieChartTOP = ({
  data = [],
  colors = [],
  width = 500,
  height = 350,
  innerRadius = 50,
  outerRadius = 70,
}) => {
  const [activeIndex, setActiveIndex] = useState(null);

  // const onPieEnter = (_, index) => setActiveIndex(index);
  // const onPieLeave = () => setActiveIndex(null);
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const value = payload[0]?.payload?.OffLoadingCycle;
      const time = minutesToTimeStrings(value);
      const recName =
        payload[0]?.payload?.RECNAME || payload[0]?.payload?.FNAME;
      return (
        <div
          style={{
            background: "#E7E7ED",
            color: "#5F6D8C",
            padding: "10px",
            borderRadius: "8px",
            fontSize: "12px",
            // minWidth: "60px",
            textAlign: "center",
            boxShadow: "0px 2px 10px rgba(0,0,0,0.3)",
          }}
        >
          <p style={{ margin: 0 }}>{`${recName} ${time}`}</p>
        </div>
      );
    }
    return null;
  };
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    index,
  }) => {
    if (!data[index]) return null;

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 2;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    const isRightSide = Math.cos(-midAngle * RADIAN) > 0;
    const lineLength = isRightSide ? 18 : -18;
    const textOffset = isRightSide ? 18 : -18;
    const color = colors[index % colors.length] || "#ccc";
    const squareSize = 10;

    const fullName = data[index].RECNAME || data[index].FNAME;
    const words = fullName?.split(" ") || [];
    const firstLine = words.slice(0, 2).join(" ");
    const secondLine = words.slice(2).join(" ");
    const timeLabel = minutesToTimeString(data[index].OffLoadingCycle);

    const lineSpacing = 12; // <-- Increased spacing to prevent overlap

    return (
      <>
        <line
          x1={x}
          y1={y}
          x2={x + lineLength}
          y2={y}
          stroke="#ccc"
          strokeWidth={1}
        />
        <rect
          x={x + lineLength + (isRightSide ? 0 : -squareSize)}
          y={y - squareSize / 2}
          width={squareSize}
          height={squareSize}
          fill={color}
          rx={2}
          ry={2}
        />
        {/* <text
        x={x + lineLength + textOffset}
        y={y - lineSpacing}
        fill="#5F6D8C"
        textAnchor={isRightSide ? "start" : "end"}
        fontSize={11}
      >
        {firstLine}
      </text> */}
        {/* First line (Name + time) */}
        <text
          x={x + lineLength + textOffset}
          y={y - lineSpacing + 15} // draw this ABOVE
          fill="#5F6D8C"
          textAnchor={isRightSide ? "start" : "end"}
          fontSize={11}
        >
          {`${firstLine} (${timeLabel})`}
        </text>

        {/* Second line below only if exists */}
        {secondLine && (
          <text
            x={x + lineLength + textOffset}
            y={y + 15} // draw slightly below first line
            fill="#5F6D8C"
            textAnchor={isRightSide ? "start" : "end"}
            fontSize={11}
          >
            {secondLine}
          </text>
        )}
      </>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          cx={350}
          cy={height / 2}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          // paddingAngle={-15}
          // cornerRadius={40}
          dataKey="OffLoadingCycle"
          label={renderCustomizedLabel}
          // onMouseEnter={onPieEnter}
          // onMouseLeave={onPieLeave}
          style={{ cursor: "pointer" }}
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={colors[index % colors.length]}
              stroke={
                index === activeIndex ? colors[index % colors.length] : "none"
              }
              strokeWidth={index === activeIndex ? 3 : 1}
            />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
      </PieChart>
    </ResponsiveContainer>
  );
};
//Offloading wait time(hr) vertical bar chart

export const OffloadingWaitTimeChart = ({
  data,
  width,
  height,
  barColor,
  // dataKey,
}) => {
  const minutesToTimeStrings = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}:${minutes.toString().padStart(2, "0")}`;
  };
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const value = payload[0]?.payload?.Completed;
      const time = minutesToTimeStrings(value);
      const recName = payload[0]?.payload?.RECNAME;
      return (
        <div
          style={{
            background: "#E7E7ED",
            color: "#5F6D8C",
            padding: "10px",
            borderRadius: "8px",
            fontSize: "12px",
            // minWidth: "60px",
            textAlign: "center",
            boxShadow: "0px 2px 10px rgba(0,0,0,0.3)",
          }}
        >
          <p style={{ margin: 0 }}>{`${time}`}</p>
        </div>
      );
    }
    return null;
  };
  return (
    <div>
      <ResponsiveContainer width={width} height={height}>
        <BarChart
          layout="vertical"
          margin={{ top: 20, right: 30, left: 10, bottom: 10 }}
          barSize={28}
          data={data}
          style={{ cursor: "pointer" }}
        >
          <CartesianGrid vertical={false} horizontal={false} />
          <XAxis type="number" axisLine={false} tickLine={false} hide />

          <YAxis
            type="category"
            dataKey={(item) => item.RECNAME || item.FNAME}
            axisLine={false}
            tickLine={false}
            width={150}
          />
          <Tooltip content={<CustomTooltip />} />

          <Bar dataKey="Completed" fill={barColor} radius={5}>
            <LabelList
              content={({ x, y, width, value }) => (
                <text
                  x={x + width + 5} // 5px padding to the right of the bar
                  y={y + 14} // vertically center the label in the bar
                  fill="#000"
                  fontSize={12}
                >
                  {minutesToTimeStrings(value)}
                </text>
              )}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

//time after loading(Hr) PieChart
export const TimeAfterLoadingPieChart = ({
  data = [],
  colors = [],
  height,
  width,
}) => {
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    index,
  }) => {
    if (!data[index]) return null; // Prevent errors if index is out of bounds
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 1.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const isHrLine = Math.cos(-midAngle * RADIAN) > 0;
    const lineLength = isHrLine ? 20 : -20;
    const textOffset = isHrLine ? 5 : -5;
    const color = colors[index % colors.length] || "#ccc";
    const squareSize = 12;

    return (
      <>
        <line
          x1={x}
          y1={y}
          x2={x + lineLength}
          y2={y}
          stroke="#ccc"
          strokeWidth={1}
        />
        <rect
          x={x + lineLength + (isHrLine ? -squareSize - 0 : 0)}
          y={y - squareSize / 2}
          width={squareSize}
          borderRadius={5}
          height={squareSize}
          fill={color}
          rx={2}
          ry={2}
        />
        <text
          x={x + lineLength + textOffset}
          y={y}
          fill="#5F6D8C"
          textAnchor={isHrLine ? "start" : "end"}
          dominantBaseline="central"
          fontSize={10}
        >
          {`${data[index].RECNAME || data[index].FNAME} `}
        </text>
      </>
    );
  };

  return (
    <div>
      <ResponsiveContainer width={width} height={height}>
        <PieChart>
          <Pie
            dataKey="Completed"
            startAngle={180}
            endAngle={0}
            data={data}
            cx="50%"
            cy="70%"
            outerRadius={90}
            innerRadius={50}
            label={renderCustomizedLabel}
            style={{ cursor: "pointer" }}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={colors[index % colors.length]}
              />
            ))}
          </Pie>
          <text
            x="50%" // Center the text horizontally
            y="70%" // Center the text vertically
            textAnchor="middle" // Align text to the middle
            dominantBaseline="middle" // Center the text vertically
            fontSize={16} // Adjust font size as needed
            fill="#5F6D8C"
          ></text>
          <Tooltip content={<CustomTooltips />} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};
//Loading Time(Hr)

const CustomDot = (props) => {
  const { cx, cy, payload, fill } = props;
  const value = payload.Completed;
  const label = minutesToTimeStrings(value);

  return (
    <g>
      <circle cx={cx} cy={cy} r={8} fill={fill} />
      <text
        x={cx}
        y={cy - 12} // position above the dot
        textAnchor="middle"
        fontSize={12}
        fill="#000"
      >
        {label}
      </text>
    </g>
  );
};
const CustomDotTOP = (props) => {
  const { cx, cy, payload, fill } = props;
  const value = payload.LoadingCycle;
  const label = minutesToTimeStrings(value);

  return (
    <g>
      <circle cx={cx} cy={cy} r={8} fill={fill} />
      <text
        x={cx}
        y={cy - 12} // position above the dot
        textAnchor="middle"
        fontSize={12}
        fill="#000"
      >
        {label}
      </text>
    </g>
  );
};
const minutesToTimeStrings = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${minutes.toString().padStart(2, "0")}`;
};
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const value = payload[0]?.payload?.Completed;
    const time = minutesToTimeStrings(value);
    const recName = payload[0]?.payload?.RECNAME;
    return (
      <div
        style={{
          background: "#E7E7ED",
          color: "#5F6D8C",
          padding: "10px",
          borderRadius: "8px",
          fontSize: "12px",
          // minWidth: "60px",
          textAlign: "center",
          boxShadow: "0px 2px 10px rgba(0,0,0,0.3)",
        }}
      >
        <p style={{ margin: 0 }}>{`${time}`}</p>
      </div>
    );
  }
  return null;
};
const CustomTooltipTop = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const value = payload[0]?.payload?.LoadingCycle;
    const time = minutesToTimeStrings(value);
    const recName = payload[0]?.payload?.RECNAME;
    return (
      <div
        style={{
          background: "#E7E7ED",
          color: "#5F6D8C",
          padding: "10px",
          borderRadius: "8px",
          fontSize: "12px",
          // minWidth: "60px",
          textAlign: "center",
          boxShadow: "0px 2px 10px rgba(0,0,0,0.3)",
        }}
      >
        <p style={{ margin: 0 }}>{`${time}`}</p>
      </div>
    );
  }
  return null;
};
// const formatedLabel = (value) => {
//   const maxCharsPerLine = 10;
//   const regex = new RegExp(`.{1,${maxCharsPerLine}}`, 'g');
//   return value.match(regex);
// };
// Utility: Break text by space, keeping words together
const formatedLabel = (text, maxLineLength = 15) => {
  if (!text) return [];

  const words = text.split(" ");
  const lines = [];
  let currentLine = "";

  for (let word of words) {
    if ((currentLine + " " + word).trim().length <= maxLineLength) {
      currentLine += (currentLine ? " " : "") + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
};

export const LoadingTimeChart = ({ data, color, height, width = "100%" }) => {
  return (
    <ResponsiveContainer width={width} height={height}>
      <ScatterChart
        margin={{
          top: 30,
          right: 20,
          bottom: 40,
          left: 0,
        }}
        style={{ cursor: "pointer" }}
      >
        <CartesianGrid vertical={false} horizontal={false} />
        <XAxis
          type="category"
          dataKey={(item) => item.RECNAME || item.FNAME}
          interval={0}
          tick={({ x, y, payload }) => {
            const lines = formatedLabel(payload.value);
            return (
              <g transform={`translate(${x},${y})`}>
                {lines.map((line, index) => (
                  <text
                    key={index}
                    x={0}
                    y={index * 13 + 10} // adds space below axis line
                    textAnchor="middle"
                    fontSize={10}
                  >
                    {line}
                  </text>
                ))}
              </g>
            );
          }}
          axisLine={true}
          tickLine={false}
        />

        <YAxis
          dataKey="Completed"
          axisLine={false}
          tickLine={false}
          tick={false}
        />
        <Tooltip
          content={<CustomTooltip />}
          cursor={{ strokeDasharray: "3 3" }}
        />
        <Scatter
          name="Loading Time"
          data={data}
          fill={color}
          shape={<CustomDot />}
        ></Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  );
};
export const LoadingTimeChartTop = ({
  data,
  color,
  height,
  width = "100%",
}) => {
  return (
    <ResponsiveContainer width={width} height={height}>
      <ScatterChart
        margin={{
          top: 30,
          right: 20,
          bottom: 40,
          left: 0,
        }}
        style={{ cursor: "pointer" }}
      >
        <CartesianGrid vertical={false} horizontal={false} />
        <XAxis
          type="category"
          dataKey={(item) => item.RECNAME || item.FNAME}
          interval={0}
          tick={({ x, y, payload }) => {
            const lines = formatedLabel(payload.value);
            return (
              <g transform={`translate(${x},${y})`}>
                {lines.map((line, index) => (
                  <text
                    key={index}
                    x={0}
                    y={index * 13 + 10} // adds space below axis line
                    textAnchor="middle"
                    fontSize={10}
                  >
                    {line}
                  </text>
                ))}
              </g>
            );
          }}
          axisLine={true}
          tickLine={false}
        />

        <YAxis
          dataKey="LoadingCycle"
          axisLine={false}
          tickLine={false}
          tick={false}
        />
        <Tooltip
          content={<CustomTooltipTop />}
          cursor={{ strokeDasharray: "3 3" }}
        />
        <Scatter
          name="Loading Time"
          data={data}
          fill={color}
          shape={<CustomDotTOP />}
        ></Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  );
};
//Loading Waiting Time(Hr) top 5 company
export const CompainedAreaChart = ({ data, width }) => {
  const CustomDot = (props) => {
    const { cx, cy, payload, fill } = props;
    const value = payload.Completed;
    const label = minutesToTimeStrings(value);

    return (
      <g>
        <circle cx={cx} cy={cy} r={8} fill={fill} />
        <text
          x={cx}
          y={cy - 12} // position above the dot
          textAnchor="middle"
          fontSize={12}
          fill="#000"
        >
          {label}
        </text>
      </g>
    );
  };
  const minutesToTimeStrings = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}:${minutes.toString().padStart(2, "0")}`;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const value = payload[0]?.payload?.Completed;
      const time = minutesToTimeStrings(value);
      const recName = payload[0]?.payload?.RECNAME;
      return (
        <div
          style={{
            background: "#E7E7ED", // Dark background
            color: "#5F6D8C", // White text
            padding: "10px", // Add padding
            borderRadius: "8px", // Rounded corners
            fontSize: "12px", // Bigger text
            minWidth: "60px", // Ensure size
            textAlign: "center",
            boxShadow: "0px 2px 10px rgba(0,0,0,0.3)", // Soft shadow
          }}
        >
          <p style={{ margin: 0 }}>{` ${time}`}</p>
        </div>
      );
    }
    return null;
  };
  return (
    <ResponsiveContainer width={width} height={250}>
      <ComposedChart
        data={data}
        margin={{ top: 30, right: 30, bottom: 50, left: 0 }}
        style={{ cursor: "pointer" }}
      >
        <defs>
          <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#FCD570" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#FFFFFF" stopOpacity={0.8} />
          </linearGradient>
        </defs>
        <XAxis
          type="category"
          dataKey={(item) => item.RECNAME || item.FNAME}
          interval={0}
          minTickGap={7}
          tick={({ x, y, payload }) => {
            const lines = formatedLabel(payload.value);
            return (
              <g transform={`translate(${x},${y})`}>
                {lines.map((line, index) => (
                  <text
                    key={index}
                    x={0}
                    y={index * 13 + 10} // adds space below axis line
                    textAnchor="middle"
                    fontSize={10}
                  >
                    {line}
                  </text>
                ))}
              </g>
            );
          }}
          axisLine={true}
          tickLine={false}
        />

        <YAxis
          axisLine={false}
          tickLine={false}
          tick={({ x, y, payload }) => {
            const time = minutesToHour(payload.value);
            return (
              <text
                x={x - 10}
                y={y + 4}
                textAnchor="end"
                fontSize={12}
                fill="#666"
              >
                {time}
              </text>
            );
          }}
        />
        <Tooltip
          content={<CustomTooltip />}
          cursor={{ strokeDasharray: "3 3" }}
        />
        <Area
          type="Linear"
          dataKey="Completed"
          stroke="#E4A400"
          fill="url(#colorUv)"
        />
        <Scatter
          name="Waiting Time"
          dataKey="Completed"
          data={data}
          fill="#FCD570"
          shape={<CustomDot />}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};
//Loading time bottom 5
export const CompainedAreaChart2 = ({ data, width }) => {
  const CustomDot = (props) => {
    const { cx, cy, payload, fill } = props;
    const value = payload.Completed;
    const label = minutesToTimeStrings(value);

    return (
      <g>
        <circle cx={cx} cy={cy} r={8} fill={fill} />
        <text
          x={cx}
          y={cy - 12} // position above the dot
          textAnchor="middle"
          fontSize={12}
          fill="#000"
        >
          {label}
        </text>
      </g>
    );
  };
  const minutesToTimeStrings = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}:${minutes.toString().padStart(2, "0")}`;
  };
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const value = payload[0]?.payload?.Completed;
      const time = minutesToTimeStrings(value);
      const recName = payload[0]?.payload?.RECNAME;
      return (
        <div
          style={{
            background: "#E7E7ED", // Dark background
            color: "#5F6D8C", // White text
            padding: "10px", // Add padding
            borderRadius: "8px", // Rounded corners
            fontSize: "12px", // Bigger text
            minWidth: "60px", // Ensure size
            textAlign: "center",
            boxShadow: "0px 2px 10px rgba(0,0,0,0.3)", // Soft shadow
          }}
        >
          <p style={{ margin: 0 }}>{` ${time}`}</p>
        </div>
      );
    }
    return null;
  };
  return (
    <ResponsiveContainer width={width} height={250}>
      <ComposedChart
        data={data}
        margin={{ top: 30, right: 30, bottom: 50, left: 10 }}
        style={{ cursor: "pointer" }}
      >
        <defs>
          <linearGradient id="colorUvs" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#00D1E4" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#FFFFFF" stopOpacity={0.8} />
          </linearGradient>
        </defs>
        <XAxis
          type="category"
          dataKey={(item) => item.RECNAME || item.FNAME}
          interval={0}
          minTickGap={7}
          tick={({ x, y, payload }) => {
            const lines = formatedLabel(payload.value);
            return (
              <g transform={`translate(${x},${y})`}>
                {lines.map((line, index) => (
                  <text
                    key={index}
                    x={0}
                    y={index * 13 + 10} // adds space below axis line
                    textAnchor="middle"
                    fontSize={10}
                  >
                    {line}
                  </text>
                ))}
              </g>
            );
          }}
          axisLine={true}
          tickLine={false}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={({ x, y, payload }) => {
            const time = minutesToHour(payload.value);
            return (
              <text
                x={x - 10}
                y={y + 4}
                textAnchor="end"
                fontSize={12}
                fill="#666"
              >
                {time}
              </text>
            );
          }}
        />
        <Tooltip
          content={<CustomTooltip />}
          cursor={{ strokeDasharray: "3 3" }}
        />
        <Area
          type="Linear"
          dataKey="Completed"
          stroke="#00D1E4"
          fill="url(#colorUvs)"
        />
        <Scatter
          name="Waiting Time"
          dataKey="Completed"
          data={data}
          fill="#00D1E4"
          shape={<CustomDot />}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};
//Time After Loading Bar chart

export const TimeAfterBarchart = ({ data, width, height, color }) => {
  const minutesToTimeStrings = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}:${minutes.toString().padStart(2, "0")}`;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const value = payload[0]?.payload?.Completed;
      const time = minutesToTimeStrings(value);
      const recName = payload[0]?.payload?.RECNAME;
      return (
        <div
          style={{
            background: "#E7E7ED", // Dark background
            color: "#5F6D8C", // White text
            padding: "10px", // Add padding
            borderRadius: "8px", // Rounded corners
            fontSize: "12px", // Bigger text
            minWidth: "60px", // Ensure size
            textAlign: "center",
            boxShadow: "0px 2px 10px rgba(0,0,0,0.3)", // Soft shadow
          }}
        >
          <p style={{ margin: 0 }}>{`${time}`}</p>
        </div>
      );
    }
    return null;
  };
  return (
    <ResponsiveContainer width={width} height={height}>
      <BarChart
        margin={{ top: 30, right: 10, left: 10, bottom: 30 }}
        barSize={25}
        data={data}
        style={{ cursor: "pointer" }}
      >
        {/* <CartesianGrid vertical={false} horizontal={true} /> */}
        {/* <XAxis
          dataKey="RECNAME"
          axisLine={false}
          tickLine={false}
          interval={0}
          tick={({ x, y, payload }) => {
            const lines = formatLabel(payload.value);
            return (
              <g transform={`translate(${x},${y})`}>
                {Array.isArray(lines) ? (
                  lines.map((line, index) => (
                    <text
                      key={index}
                      x={0}
                      y={index * 10}
                      dy={10}
                      textAnchor="middle"
                      fontSize={12}
                    >
                      {line}
                    </text>
                  ))
                ) : (
                  <text x={0} y={0} dy={10} textAnchor="middle" fontSize={12}>
                    {lines}
                  </text>
                )}
              </g>
            );
          }}
        /> */}
        <XAxis
          dataKey={(item) => item.RECNAME || item.FNAME}
          interval={0}
          minTickGap={7}
          tick={({ x, y, payload }) => {
            const lines = formatedLabel(payload.value);
            return (
              <g transform={`translate(${x},${y})`}>
                {lines.map((line, index) => (
                  <text
                    key={index}
                    x={0}
                    y={index * 13 + 10} // adds space below axis line
                    textAnchor="middle"
                    fontSize={10}
                  >
                    {line}
                  </text>
                ))}
              </g>
            );
          }}
          axisLine={true}
          tickLine={false}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={({ x, y, payload }) => {
            const time = minutesToHour(payload.value);
            return (
              <text
                x={x - 10}
                y={y + 4}
                textAnchor="end"
                fontSize={12}
                fill="#666"
              >
                {time}
              </text>
            );
          }}
        />

        <Tooltip
          content={<CustomTooltip />}
          cursor={{ strokeDasharray: "3 3" }}
        />

        <Bar dataKey="Completed" fill={color} radius={[5, 5, 0, 0]}>
          <LabelList
            content={({ x, y, width, value }) => (
              <text y={y + width - 30} x={x} fill="#000" fontSize={12}>
                {minutesToTimeStrings(value)}
              </text>
            )}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};
